import { BRAND } from "./brand";
import { defaultDepartmentName } from "./departments";
import type { JobSiteDelivery, Product } from "./schemas";
export * from "./barcodes";
export * from "./brand";
export * from "./content";
export * from "./departments";
export * from "./features";
export * from "./pagination";
export * from "./seo";
export * from "./signIn";
export type {
  CartLine,
  CheckoutSessionRequest,
  ClientEvents,
  ContractorOrderRequest,
  ContractorPricingUpdate,
  ContractorStatusUpdate,
  DeliveryAddress,
  InventoryChange,
  InventoryUpdates,
  JobSiteDelivery,
  PosInventoryChange,
  PosInventorySnapshot,
  Product,
  ProductFeed,
  ProductImage,
  ProductQuery,
  ProfileUpdate,
  StaffOrderAction,
} from "./schemas";

/** Stripe's largest USD charge, in cents: no price or total can be above it. */
export const MAX_CHARGE_CENTS = 99_999_999;

export interface PosChangesResult {
  /** Highest sequence number Azure has applied. */
  appliedSeq: number;
  applied: number;
  /** Changes at or below the SKU's current sequence (duplicates and late retries). */
  ignored: number;
  /** SKUs that aren't in the product catalog (stored anyway, in case they appear later). */
  unknownSkus: string[];
  /** A sequence gap was seen: send a full export to re-baseline. */
  fullSyncRequested: boolean;
}

export interface PosSnapshotResult {
  /** False when a newer export was already applied (this one arrived late). */
  accepted: boolean;
  items: number;
  /** Changes newer than the export's `seq` that were kept on top of it. */
  keptChanges: number;
  appliedSeq: number;
}

export interface PosStatus {
  inventorySource: "feed" | "pos";
  appliedSeq: number;
  snapshotSeq: number | null;
  lastFullSyncAt: string | null;
  /** Changes on top of the last full export. */
  pendingChanges: number;
}

/* ------------------------------------------------------------------ */
/* Store taxonomy (mirrors the neushardware.com menus)                 */
/* ------------------------------------------------------------------ */

export interface Department {
  slug: string;
  name: string;
  /** The line under its tile on the departments page. */
  blurb: string;
  /** Shown under the "Departments" menu. */
  inMenu: boolean;
}

/** This brand's departments, in its order and with its names (brand.json `departments`). */
export const DEPARTMENTS: readonly Department[] = BRAND.departments.map((d) => ({
  slug: d.slug,
  name: d.name ?? defaultDepartmentName(d.slug),
  blurb: d.blurb,
  inMenu: d.inMenu,
}));

/** The departments this brand sells: products in any other department are hidden on its site. */
export const SOLD_DEPARTMENTS: readonly string[] = DEPARTMENTS.map((d) => d.slug);

/** A department's name on this brand's site. */
export function departmentName(slug: string): string {
  return DEPARTMENTS.find((d) => d.slug === slug)?.name ?? defaultDepartmentName(slug);
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/* ------------------------------------------------------------------ */
/* API DTOs                                                            */
/* ------------------------------------------------------------------ */

/** "relevance" (best match) applies to text searches; elsewhere it falls back to "popular". */
export const SORT_OPTIONS = ["popular", "relevance", "price-asc", "price-desc", "name-asc", "newest"] as const;
export type SortOption = (typeof SORT_OPTIONS)[number];

/* ------------------------------------------------------------------ */
/* Accounts (sign-in through Entra External ID, profiles)             */
/* ------------------------------------------------------------------ */

/** The departments a profile can mark as favorites: the brand's menu departments. */
export const FAVORITE_CATEGORIES: readonly string[] = DEPARTMENTS.filter((d) => d.inMenu).map((d) => d.slug);
export type FavoriteCategory = string;

export const ALERT_CHANNELS = ["email", "sms", "both"] as const;
export type AlertChannel = (typeof ALERT_CHANNELS)[number];

/** Shown next to the alerts checkbox and stored with the consent (TCPA). */
export const ALERT_CONSENT_TEXT = BRAND.alertConsentText;

/**
 * A same-site path for ?next= after signing in, or null. Refuses other origins, "//host", any backslash (browsers
 * read it as a slash) and control characters (browsers drop tabs and line breaks, so "/<tab>/host" is "//host").
 */
export const safeNextPath = (next: string | null | undefined) =>
  next && next.length <= 2_000 && /^\/(?!\/)/.test(next) && !/[\\\p{Cc}]/u.test(next) ? next : null;

/** The signed-in user (GET /api/me). */
export interface Me {
  id: string;
  name: string;
  /** Sign-in email from Entra External ID (verified there), if the identity provider shared one. */
  email?: string;
  /** Mobile number for text alerts (E.164), entered on the profile and not verified. */
  phone?: string;
  favoriteCategories: FavoriteCategory[];
  alerts: { optIn: boolean; channel: AlertChannel; consentAt?: string };
  contractor: {
    isContractor: boolean;
    companyName?: string;
    companyPhone?: string;
    companyEmail?: string;
    enabledAt?: string;
    /** With contractor approval on: whether an admin has activated the account (contractors only). */
    status?: ContractorStatus;
  };
  createdAt: string;
  /** Staff with the "Admin" app role in Entra (as of their last sign-in). */
  isAdmin?: boolean;
  /** Prices staff set for this contractor (read-only here; they apply while isContractor). */
  contractorPricing?: ContractorPricing;
}

/**
 * A contractor account's approval, with the contractorApproval flag on: pending until an admin activates it, and
 * inactive once an admin deactivates it. Off, every contractor account is active.
 */
export const CONTRACTOR_STATUSES = ["pending", "active", "inactive"] as const;
export type ContractorStatus = (typeof CONTRACTOR_STATUSES)[number];

/** What staff see for each status. */
export const CONTRACTOR_STATUS_LABELS: Record<ContractorStatus, string> = {
  pending: "Awaiting approval",
  active: "Active",
  inactive: "Deactivated",
};

/** Whether the contractor program is open to this user: a contractor account, and active (or approval is off). */
export const isActiveContractor = (me: Pick<Me, "contractor">) =>
  me.contractor.isContractor && (me.contractor.status ?? "active") === "active";

/* ------------------------------------------------------------------ */
/* Contractor pricing: set by staff for select contractors           */
/* ------------------------------------------------------------------ */

export interface ContractorPricing {
  /** Percent off job orders, for lines without a special price. */
  discountPercent: number;
  /** Special prices in cents, by SKU: on job orders and in regular shopping and checkout. */
  prices: Record<string, number>;
  updatedAt?: string;
  /** The staff member's email. */
  updatedBy?: string;
}

export const MAX_CONTRACTOR_DISCOUNT_PERCENT = 50;
export const MAX_SPECIAL_PRICES = 500;

/**
 * What a contractor gets: the pricing set for all contractors, with their own on top. Their own
 * special price for a product always wins (even if it's higher); their own job order discount
 * wins when it's above 0. Undefined when neither has anything.
 */
export function mergeContractorPricing(
  all: Pick<ContractorPricing, "discountPercent" | "prices"> | undefined,
  own: Pick<ContractorPricing, "discountPercent" | "prices"> | undefined,
): Pick<ContractorPricing, "discountPercent" | "prices"> | undefined {
  const prices = { ...all?.prices, ...own?.prices };
  const discountPercent = own?.discountPercent ? own.discountPercent : (all?.discountPercent ?? 0);
  return discountPercent || Object.keys(prices).length ? { discountPercent, prices } : undefined;
}

/** A product's regular price: what it costs when it isn't on sale. */
export const regularPriceOf = (p: Pick<Product, "price" | "compareAtPrice">) =>
  p.compareAtPrice !== undefined && p.compareAtPrice > p.price ? p.compareAtPrice : p.price;

/**
 * A special price set as a percent off the regular price (the admin page's "% off" calculator),
 * rounded to the cent (halves up; whole-number math, so no floating-point slips). Null for a
 * percent that isn't above 0 and below 100 with at most two decimals, or a result that wouldn't
 * be lower than the regular price and at least one cent.
 */
export function percentOffPrice(regularCents: number, percentText: string): number | null {
  const text = percentText.trim().replace(/%$/, "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const hundredths = Math.round(Number(text) * 100); // 12.5% → 1250
  if (!(hundredths > 0 && hundredths < 10_000)) return null;
  const price = Math.round((regularCents * (10_000 - hundredths)) / 10_000);
  return price >= 1 && price < regularCents ? price : null;
}

/**
 * What a shopper pays for a product: the contractor's special price when they have one that's
 * lower than the current price (sales included) and they're still a contractor, else the price.
 * `regularPrice` is set only when the special price applies, for the strikethrough.
 */
export function effectivePrice(
  product: { sku: string; price: number },
  me: Pick<Me, "contractor" | "contractorPricing"> | null | undefined,
): { price: number; regularPrice?: number } {
  const special = me?.contractor.isContractor ? me.contractorPricing?.prices[product.sku] : undefined;
  return special !== undefined && special < product.price
    ? { price: special, regularPrice: product.price }
    : { price: product.price };
}

/** A contractor on the admin page (GET /api/staff/contractors/{id}). */
export interface AdminContractor {
  id: string;
  name: string;
  email?: string;
  companyName?: string;
  companyPhone?: string;
  companyEmail?: string;
  isContractor: boolean;
  contractorPricing?: ContractorPricing;
  /** With contractor approval on: the account's status, and who last changed it and when. */
  status?: ContractorStatus;
  statusChangedAt?: string;
  statusChangedBy?: string;
}

/** A contractor admin list row (GET /api/staff/contractors). */
export interface AdminContractorSummary {
  id: string;
  name: string;
  email?: string;
  companyName?: string;
  discountPercent: number;
  specialPrices: number;
  /** With contractor approval on. */
  status?: ContractorStatus;
}

/* ------------------------------------------------------------------ */
/* Contractor program: job orders, not limited by stock               */
/* ------------------------------------------------------------------ */

/** Job orders must be needed at least this many days out, counted in the store's timezone. */
export const CONTRACTOR_LEAD_DAYS = 14;
export const STORE_TIME_ZONE = BRAND.timeZone;

/**
 * The start of a brand's order numbers (NEU-MH2K9Q1A-3F9C0B12): the first three letters or digits of its id,
 * in capitals ("neus" → NEU, "whitelabel" → WHI). Always three, so it can't be a job order's CO-.
 */
export const orderPrefix = (brandId: string) =>
  brandId
    .replace(/[^a-z0-9]/gi, "")
    .slice(0, 3)
    .toUpperCase();
export const ORDER_PREFIX = orderPrefix(BRAND.id);

/** Today's date (YYYY-MM-DD) in the store's timezone. */
export function storeDate(now: number): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone: STORE_TIME_ZONE }).format(now);
}

/** The earliest needed-by date (YYYY-MM-DD) a contractor order can have today. */
export function earliestNeededBy(now: number): string {
  const [y, m, d] = storeDate(now).split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + CONTRACTOR_LEAD_DAYS)).toISOString().slice(0, 10);
}

/** "08:30" → "8:30 am". */
export function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number) as [number, number];
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
}

/** The weekday (0 = Sunday) of a YYYY-MM-DD date. */
export const weekday = (date: string) => new Date(`${date}T12:00:00Z`).getUTCDay();

export const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * The store's opening hours by weekday (0 = Sunday), null when closed, from the brand. The one
 * source for the hours shown on the site and for when job orders can be delivered.
 */
export const STORE_OPEN_HOURS: readonly (Readonly<{ open: string; close: string }> | null)[] = Object.freeze(
  BRAND.openHours.map((h) => (h ? Object.freeze({ ...h }) : null)),
);

/** The store's hours on a YYYY-MM-DD date, or null when it's closed. */
export const storeHoursOn = (date: string) => STORE_OPEN_HOURS[weekday(date)] ?? null;

/** The hours for display, with days that share hours grouped: Monday – Friday, Saturday, Sunday. */
export const STORE_HOURS_SUMMARY: { days: string; hours: string }[] = (() => {
  const hoursText = (h: (typeof STORE_OPEN_HOURS)[number]) =>
    h ? `${formatTime(h.open)} – ${formatTime(h.close)}` : "Closed";
  // Monday first, Sunday last, as stores list them.
  const order = [1, 2, 3, 4, 5, 6, 0];
  const groups: { first: number; last: number; hours: string }[] = [];
  for (const d of order) {
    const hours = hoursText(STORE_OPEN_HOURS[d] ?? null);
    const prev = groups.at(-1);
    if (prev && prev.hours === hours) prev.last = d;
    else groups.push({ first: d, last: d, hours });
  }
  return groups.map((g) => ({
    days: g.first === g.last ? WEEKDAY_NAMES[g.first]! : `${WEEKDAY_NAMES[g.first]} – ${WEEKDAY_NAMES[g.last]}`,
    hours: g.hours,
  }));
})();

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number) as [number, number];
  return h * 60 + m;
};
const fromMinutes = (n: number) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
const openDays = STORE_OPEN_HOURS.filter((h) => h !== null);

/** Every delivery time (half-hour steps) the store is open on some day. */
export const DELIVERY_TIMES: string[] = (() => {
  const start = Math.min(...openDays.map((h) => toMinutes(h.open)));
  const end = Math.max(...openDays.map((h) => toMinutes(h.close)));
  return Array.from({ length: (end - start) / 30 + 1 }, (_, i) => fromMinutes(start + i * 30));
})();

/** Delivery times on a date: within the store's hours that day (none when it's closed). */
export function deliveryTimesOn(date: string): string[] {
  const hours = storeHoursOn(date);
  return hours ? DELIVERY_TIMES.filter((t) => t >= hours.open && t <= hours.close) : [];
}

/**
 * Why a delivery date and window don't fit the store's hours, or null when they do. Null for a date
 * that isn't a real day too: the date's own validation reports that.
 */
export function deliveryHoursProblem(date: string, window: { from: string; to: string }): string | null {
  const day = WEEKDAY_NAMES[weekday(date)];
  if (!day) return null;
  const hours = storeHoursOn(date);
  if (!hours) return `The store is closed on ${day}s, so we can't deliver then. Choose another day.`;
  if (window.from < hours.open || window.to > hours.close) {
    return `On ${day}s we deliver between ${formatTime(hours.open)} and ${formatTime(hours.close)}, while the store is open.`;
  }
  return null;
}

/**
 * A phone number as typed, in E.164. Ten-digit numbers (and 11 digits starting with 1) are US
 * numbers; anything else must start with + and a country code. Null when it isn't a number.
 * Shared so the browser and the API accept exactly the same numbers.
 */
export function parsePhone(input: string): string | null {
  const text = input.trim();
  if (!/^[+\d\s().-]+$/.test(text)) return null;
  const digits = text.replace(/\D/g, "");
  if (text.startsWith("+")) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

/** US states, DC and territories, for delivery addresses (and so tax is asked for a real place). */
export const US_STATES = new Set(
  "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA PR RI SC SD TN TX UT VT VA VI WA WV WI WY GU AS MP".split(
    " ",
  ),
);

/** Prices in cents, all from the catalog on the server. */
export interface ContractorQuote {
  lines: PricedLine[];
  subtotal: number;
  /** Null until Stripe Tax can calculate it: shown as "calculated at confirmation". */
  tax: number | null;
  /** Stocking/shipping fee, or the delivery fee with job-site delivery; shown as its own line. */
  fee: number;
  /** Discounts, as a positive amount taken off: the contractor's job order discount. */
  discount: number;
  /** The job order discount: percent off lines without a special price (orders before 0.1.15 don't keep it). */
  discountPercent?: number;
  total: number;
  depositPercent: number;
  /** Due now, when the order is placed. */
  deposit: number;
  /** Due later (plus tax, when it's still pending). */
  balance: number;
}

/** "placed": taken without a deposit (the contractorDeposits flag off), to be paid in full at pickup or delivery. */
export const CONTRACTOR_ORDER_STATUSES = ["awaiting_deposit", "deposit_paid", "balance_invoiced", "placed"] as const;
export type ContractorOrderStatus = (typeof CONTRACTOR_ORDER_STATUSES)[number];

export interface ContractorOrder {
  id: string;
  title: string;
  description: string;
  /** Needed by (pickup), or the delivery date with delivery. */
  neededBy: string;
  /** Job-site delivery (phone in E.164); absent for pickup at the store. */
  delivery?: JobSiteDelivery;
  /** The contractor kept it anonymous in the store: its pick sheet shows only the title. */
  anonymous?: boolean;
  quote: ContractorQuote;
  status: ContractorOrderStatus;
  createdAt: string;
  depositPaidAt?: string;
}

export interface ContractorOrderCreated {
  order: ContractorOrder;
  /** For paying the deposit with Stripe Elements (Checkout Session, ui_mode: elements); none without deposits. */
  clientSecret?: string;
}

/** One type-ahead result for the search box (GET /api/search/suggest). */
export interface SearchSuggestion {
  sku: string;
  name: string;
  brand: string;
  price: number;
  inventory: number;
  image?: string;
}

export interface ProductListResponse {
  items: Product[];
  total: number;
  page: number;
  pageSize: number;
  catalogVersion: number;
}

export interface FacetCount {
  slug: string;
  name: string;
  count: number;
}

export interface FacetsResponse {
  departments: FacetCount[];
  brands: FacetCount[];
}

export interface InventoryResponse {
  version: number;
  /** True when the client's version is too old (e.g. after a full refresh) and it should refetch everything. */
  reset: boolean;
  changes: { sku: string; inventory: number }[];
}

export interface PricedLine {
  sku: string;
  name: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  /** Set when a contractor's special price applies: the usual unit price, shown struck through. */
  regularUnitPrice?: number;
}

/**
 * A Stripe Checkout Session (ui_mode: elements). Totals, tax and shipping are owned by
 * the session; the client reads them from the Checkout Elements SDK.
 */
export interface CheckoutSessionResponse {
  clientSecret: string;
  sessionId: string;
  /** Whether the cart has items that ship (gift-card-only carts don't). */
  requiresShipping: boolean;
}

export interface StockProblem {
  sku: string;
  requested: number;
  available: number;
  reason: "not_found" | "insufficient_stock";
}

export interface CheckoutErrorResponse {
  error: string;
  problems?: StockProblem[];
}

/** Where an online order ships (from Checkout). */
export interface ShipTo {
  name: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface Order {
  id: string;
  /** The Checkout Session; for a pay-in-store pickup order, its own id (`pickup_…`). */
  checkoutSessionId: string;
  paymentIntentId?: string;
  /** Unpaid: a pay-in-store pickup order, rung up at the register as a new purchase. */
  status: "paid" | "unpaid";
  email?: string;
  /** Pay-in-store pickup orders: the shopper's name from their account. */
  name?: string;
  lines: PricedLine[];
  subtotal: number;
  shipping: number;
  tax: number;
  total: number;
  currency: string;
  createdAt: string;
  /** Orders recorded before this was kept don't have it. */
  shipTo?: ShipTo;
}

/* ------------------------------------------------------------------ */
/* Fulfillment: staff gather, then hand over, online and job orders    */
/* ------------------------------------------------------------------ */

/** open → ready (every item collected) → picked_up or delivered. */
export const FULFILLMENT_STATUSES = ["open", "ready", "picked_up", "delivered"] as const;
export type FulfillmentStatus = (typeof FULFILLMENT_STATUSES)[number];
export const FULFILLMENT_OUTCOMES = ["picked_up", "delivered"] as const;
export type FulfillmentOutcome = (typeof FULFILLMENT_OUTCOMES)[number];

/** Staff-only: never sent to the shopper or contractor (the notes are internal). */
export interface Fulfillment {
  /** Collected lines by SKU: when, and by whom (staff email). */
  collected: Record<string, { at: string; by: string }>;
  notes: string;
  /** Set when the order leaves the store. */
  completed?: { outcome: FulfillmentOutcome; at: string; by: string };
  updatedAt?: string;
  updatedBy?: string;
}

export const EMPTY_FULFILLMENT: Fulfillment = { collected: {}, notes: "" };

/** The status follows from the record: handed over, every line collected (ready), or still open. */
export function fulfillmentStatus(f: Fulfillment, lines: { sku: string }[]): FulfillmentStatus {
  if (f.completed) return f.completed.outcome;
  return lines.length > 0 && lines.every((l) => f.collected[l.sku]) ? "ready" : "open";
}

export const isCompletedStatus = (s: FulfillmentStatus) => s === "picked_up" || s === "delivered";

/** An online order or a job order, as staff see it on the fulfillment pages. */
export interface StaffOrder {
  kind: "store" | "job";
  /** The id in the staff URLs: the Checkout Session for online orders, the order id for job orders. */
  key: string;
  /** <ORDER_PREFIX>-… (NEU-… for Neu's) or CO-…, as the customer sees it. */
  number: string;
  /** "Online order", "Pickup order (pay in store)", or the job order's title. */
  title: string;
  customer: { name?: string; company?: string; email?: string };
  placedAt: string;
  /** Job orders: the needed-by (or delivery) date. */
  neededBy?: string;
  delivery?: JobSiteDelivery;
  /** Job orders: the contractor asked for an anonymous pick sheet (only the title and QR code). */
  anonymous?: boolean;
  /** Job orders: the contractor's description. */
  description?: string;
  /**
   * Not paid yet (ring it up as a new purchase): a pay-in-store pickup order, or a job order placed without a
   * deposit (paid in full at pickup or delivery).
   */
  unpaid?: boolean;
  /** Online orders: the shipping address, and what shipping cost. */
  shipTo?: ShipTo;
  shipping?: number;
  /** Job orders: the balance after the deposit, and whether it's been invoiced. */
  balance?: { amount: number; invoiced: boolean };
  /**
   * Prices as the order recorded them; `regularUnitPrice` when a contractor special price applied, and `discount`
   * (a job order's percent off this line) in cents. From the live catalog: `gtin` for barcodes, and `location`
   * (where it is in the store) with storeLocations on.
   */
  lines: {
    sku: string;
    name: string;
    qty: number;
    unitPrice: number;
    regularUnitPrice?: number;
    discount?: number;
    gtin?: string;
    location?: string;
  }[];
  /** The customer's contractor pricing on this order: how many special-priced lines, and a job order's discount. */
  contractorPricing?: { specialPrices: number; discountPercent?: number; discount: number };
  total: number;
  status: FulfillmentStatus;
  fulfillment: Fulfillment;
}

export const MAX_FULFILLMENT_NOTES = 4000;

export function formatMoney(cents: number, currency = "usd"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
}
